import kubernetesBrandImageSrc from './images/kubernetes-brands.svg';
import { toggleSidebar } from './js/portal/sidebar';
import { CONF } from './js/portal/environment';
import { linkWithoutSearch } from './js/portal/main';

function MenuItem({
  level,
  text,
  hash,
  externalLink,
  linkId,
  itemId,
  icon,
  iconSrc,
  iconAlt,
  visible,
  deprecated,
  announce,
  children,
  featureFlag,
  microServiceRoute,
  hidden, // TODO: visible is not working properly, maybe we want to combine visible and hidden later
}) {
  if (!level) level = 1;

  let iconElement;
  if (icon) {
    iconElement = <i class={`sidebar-item-icon fas fa-fw fa-${icon}`} />;
  } else if (iconSrc) {
    iconElement = <img src={iconSrc} class="sidebar-item-icon" alt={iconAlt ?? ''} />;
  }

  const deprecationBadge = deprecated && <span class="sidebar-badge badge bg-secondary">Deprecated</span>;
  const newBadge = announce && <span class="sidebar-badge badge bg-secondary">NEW</span>;

  return (
    <li
      id={itemId}
      data-feature-flag={featureFlag}
      class={`sidebar-item sidebar-item-level${level} ${hidden ? 'd-none' : ''}`}
      style={visible ? '' : { display: 'none' }}>
      {hash && (
        <a href={linkWithoutSearch(hash)} data-hash={hash}>
          {[iconElement, text, deprecationBadge, newBadge]}
        </a>
      )}
      {externalLink && (
        <a id={linkId} href={externalLink} target="_blank" rel="noopener noreferrer">
          {text}
          <i
            class="fas fa-external-link-alt"
            style="margin-left: 0.5em; font-size: smaller; align-self: center; margin-top: -0.1em"
          />
        </a>
      )}
      {microServiceRoute && <a href={microServiceRoute}>{[iconElement, text, deprecationBadge, newBadge]}</a>}
      {children && (
        <>
          <a
            class="sidebar-menu-title collapsed"
            role="button"
            data-bs-toggle="collapse"
            data-bs-target={`#${itemId}_menu`}
            aria-expanded="false"
            aria-controls={`${itemId}_menu`}>
            {[iconElement, text, deprecationBadge, newBadge]}
          </a>
          <ul id={`${itemId}_menu`} class="sidebar-menu collapse">
            {
              /* Move child menu items to the next level */
              (Array.isArray(children) ? children : [children]).map(incrementChildItemLevel)
            }
          </ul>
        </>
      )}
    </li>
  );
}

/**
 * Adjust the hierarchy level on a child node.
 *
 * With our current JSX implementation, the child nodes are immediately rendered as DOM elements without knowing
 * about the parent context. For the sidebar menu, the parent "knows" that the children will have to be moved one
 * level down, but at that point they are already rendered as `<li>` elements, so we have to recursively modify the
 * `sidebar-item-levelX` class on each child item.
 *
 * @param childNode {HTMLElement} the child node to modify
 * @return the modified child node
 */
function incrementChildItemLevel(childNode) {
  if (childNode instanceof HTMLElement) {
    childNode.classList.replace('sidebar-item-level2', 'sidebar-item-level3');
    childNode.classList.replace('sidebar-item-level1', 'sidebar-item-level2');
    childNode.childNodes.forEach(incrementChildItemLevel);
  }
  return childNode;
}

export default function Sidebar() {
  const sidebar = (
    <div
      className="sidebar flex-column navbar-collapse d-md-block collapse sidebar-loading"
      role="navigation"
      id="sidebar">
      <ul className="sidebar-menu">
        {/* Dashboard Menu */}
        <MenuItem visible text="Dashboard" hash="#dashboard" itemId="sidebarItemDashboard" icon="tachometer-alt" />

        {/* Orders Menu */}
        <MenuItem text="Orders" itemId="sidebarItemOrders" icon="cube">
          <MenuItem text="Order Details" hash="#orderdetails" itemId="sidebarItemOrderDetails" />
          <MenuItem text="List Orders" hash="#orders" itemId="sidebarItemListOrders" />
        </MenuItem>

        {/* Accounts Menu */}
        <MenuItem text="AWS Accounts" itemId="sidebarItemAccounts" icon="cloud">
          <MenuItem text="AWS Account Details" hash="#accountdetails" itemId="sidebarItemAccountDetails" />
          <MenuItem text="List AWS Accounts" hash="#listaccounts" itemId="sidebarItemListAccounts" />
          <MenuItem
            text="Order AWS Account"
            externalLink={CONF.awsLoginDetails.myBMWItUrl}
            linkId="sidebarItemCreateAccountLink"
            itemId="sidebarItemCreateAccount"
          />
          <MenuItem text="Close AWS Account" hash="#closeaccount" itemId="sidebarItemCloseAccount" />
          <MenuItem text="Request ICP Exception" hash="#icpaccountexception" itemId="sidebarItemIcpAccountException" />
        </MenuItem>

        {/* Access Maganement */}
        <MenuItem text="  Access Management" itemId="sidebarItemIamCenter" icon="sign-in">
          <MenuItem text="List Users" microServiceRoute="/idc/list-users" itemId="sidebarItemIdcListUsers" />
          <MenuItem text="List Roles" microServiceRoute="/idc/list-roles" itemId="sidebarItemIdcListRoles" />
          <MenuItem
            text="Order Custom Role"
            microServiceRoute="/idc/order-custom-role"
            itemId="sidebarItemIdcOrderCustomRole"
          />
          <MenuItem
            hidden
            text="Update Custom Role"
            microServiceRoute="/idc/update-custom-role"
            itemId="sidebarItemIdcUpdateCustomRole"
          />
          <MenuItem
            text="List Custom Roles"
            microServiceRoute="/idc/list-custom-roles"
            itemId="sidebarItemIdcListCustomRoles"
          />
          <MenuItem text="IAM Service Users" itemId="sidebarItemServiceUsers">
            <MenuItem text="Create IAM Service User" hash="#createserviceuser" itemId="sidebarItemCreateServiceUser" />
            <MenuItem text="Delete IAM Service User" hash="#deleteserviceuser" itemId="sidebarItemDeleteServiceUser" />
          </MenuItem>
          <MenuItem
            text="Order Account Area Custom Role"
            microServiceRoute="/idc/order-custom-role-account-area"
            featureFlag="idc.custom_role_account_area"
            itemId="sidebarItemIdcOrderCustomRoleAccountArea"
          />
          <MenuItem
            text="List Account Area Custom Roles"
            microServiceRoute="/idc/list-custom-roles-account-area"
            featureFlag="idc.custom_role_account_area"
            itemId="sidebarItemIdcListCustomRolesAccountArea"
          />
          <MenuItem
            hidden
            text="Update Account Area Custom Role"
            microServiceRoute="/idc/update-custom-role-account-area"
            featureFlag="idc.custom_role_account_area"
            itemId="sidebarItemIdcUpdateCustomRoleAccountArea"
          />
        </MenuItem>

        {/* Networks Menu */}
        <MenuItem text="Networks / VPCs" itemId="sidebarItemNetworks" icon="random">
          <MenuItem text="Network Details" hash="#networkdetails" itemId="sidebarItemNetworkDetails" />
          <MenuItem text="List Direct Connect Gateways" hash="#listdxgw" itemId="sidebarItemListDXGWs" />
          <MenuItem text="List VPCs" hash="#listvpc" itemId="sidebarItemListVPCs" />
          <MenuItem
            text="Create IPv6 VPC"
            microServiceRoute="/network/create-ipv6-vpc"
            featureFlag="network.enabled"
            itemId="sidebarItemIpv6VPC"
          />
          <MenuItem text="Create VPC" hash="#createnetwork" itemId="sidebarItemCreateVPC" />
          <MenuItem text="Destroy VPC" hash="#destroyvpc" itemId="sidebarItemDestroyVPC" />
          <MenuItem text="Compare VPCs" hash="#comparevpc" itemId="sidebarItemCompareVPCs" />
          <MenuItem text="Create VPC Peering" hash="#createpeering" itemId="sidebarItemCreateVPCPeering" />
          <MenuItem text="Destroy VPC Peering" hash="#destroypeering" itemId="sidebarItemDestroyVPCPeering" />
        </MenuItem>

        {/* FQDN Management */}
        <MenuItem text="Internet Egress" itemId="sidebarItemFqdnManagement" featureFlag="fqdn.enabled" icon="globe">
          <MenuItem
            text="Create Internet Firewall Blocking Report"
            microServiceRoute="/fqdn/create-fqdn-blocking-report"
            itemId="sidebarItemCreateFQDNProxyReport"
            featureFlag="fqdn.blocklist"
          />
          <MenuItem
            text="Internet Firewall Blocking Report"
            microServiceRoute="/fqdn/list-fqdn-blocking-reports"
            itemId="sidebarItemListFQDNProxyReports"
            featureFlag="fqdn.blocklist"
          />
          <MenuItem
            text="Request Internet Firewall Allowlist"
            microServiceRoute="/fqdn/request-fqdn-allowlist"
            itemId="sidebarItemRequestFQDNAllowlist"
            featureFlag="fqdn.allowlist"
          />
          <MenuItem
            text="List Internet Firewall Allowlist"
            microServiceRoute="/fqdn/list-fqdn-allowlist"
            itemId="sidebarItemManageFQDNAllowlist"
            featureFlag="fqdn.allowlist"
          />
          <MenuItem
            text="Remove Internet Firewall Allowlist"
            microServiceRoute="/fqdn/remove-fqdn-allowlist"
            itemId="sidebarItemManageFQDNAllowlist"
            featureFlag="fqdn.allowlist"
          />
          <MenuItem
            text="Internet Firewall Recertification"
            microServiceRoute="/fqdn/fqdn-recertification"
            itemId="sidebarItemFQDNRecertification"
            featureFlag="fqdn.recertification"
          />
        </MenuItem>

        {/* Domains Menu */}
        <MenuItem text="DNS / Hosted Zones" itemId="sidebarItemDNS" icon="wrench">
          <MenuItem text="Hosted Zone Details" hash="#hostedzonedetails" itemId="sidebarItemHostedZonesDetails" />
          <MenuItem text="List Hosted Zones" hash="#listhostedzone" itemId="sidebarItemListHostedZones" />
          {/* <MenuItem text="Check Hosted Zone" hash="#checkhostedzone" itemId="sidebarItemCheckHostedZones" /> */}
          <MenuItem text="Create Hosted Zone" hash="#createhostedzone" itemId="sidebarItemCreateHostedZones" />
          <MenuItem text="Destroy Hosted Zone" hash="#destroyhostedzone" itemId="sidebarItemDestroyHostedZones" />
          <MenuItem text="Associate Hosted Zone" hash="#associatehostedzone" itemId="sidebarItemAssociateHostedZones" />
          <MenuItem text="Onboard Hosted Zone" hash="#onboardhostedzone" itemId="sidebarItemOnboardHostedZones" />
        </MenuItem>

        {/* 4wheels Menu */}
        <MenuItem
          text="4wheels Guided"
          itemId="sidebarItem4Wheels"
          iconSrc={kubernetesBrandImageSrc}
          iconAlt="Kubernetes Logo"
          featureFlag="4wheels.enabled">
          <MenuItem text="Cluster Details" hash="#4wheelsdetails" itemId="sidebarItem4WheelsDetails" />
          <MenuItem text="List Clusters" hash="#list4wheels" itemId="sidebarItemList4Wheels" />
          <MenuItem
            text="Create Cluster"
            hash="#create4wheels"
            itemId="sidebarItemCreate4Wheels"
            featureFlag="!4wheels.read_only"
          />
        </MenuItem>

        {/* Marketplace Menu */}
        <MenuItem
          text="Marketplace"
          hash="#marketplacepurchases"
          itemId="sidebarItemMarketplacePurchases"
          icon="shopping-cart"
          featureFlag="grant-purchase-role.enabled"
        />

        {/* rAI Chat Menu */}
        <MenuItem
          text="rAI Chat"
          hash="#raichat"
          itemId="sidebarItemRaiChat"
          icon="robot"
          featureFlag="gaia-chatbot.enabled"
        />

        {/* SIEM Menu */}
        <MenuItem text="SIEM" itemId="sidebarItemSiemMenu" icon="clipboard-check">
          <MenuItem text="Onboarding Request" hash="#siemonboarding" itemId="sidebarItemSiemOnboarding" />
        </MenuItem>

        {/* Trainings Menu */}
        <MenuItem text="Trainings" itemId="sidebarItemTrainingsMenu" icon="book">
          <MenuItem text="List Training Accounts" hash="#listTrainingAccounts" itemId="sidebarItemTrainingAccounts" />
          <MenuItem text="List Trainings" hash="#listTrainings" itemId="sidebarItemTrainingsList" />
          <MenuItem text="Plan Trainings" hash="#planTraining" itemId="sidebarItemTrainingsPlan" />
        </MenuItem>

        {/* Support Menu */}
        <MenuItem text="Support" itemId="sidebarItemSupportMenu" icon="question-circle">
          <MenuItem
            text="Documentation"
            externalLink="https://developer.bmwgroup.net/docs/public-cloud-platform-aws/"
            linkId="sidebarItemSupportDocumentationLink"
            itemId="sidebarItemSupportDocumentation"
          />
          <MenuItem
            text="Ask a Question"
            externalLink="https://atc.bmwgroup.net/confluence/display/DEVOPSPF/questions/all"
            linkId="sidebarItemSupportQuestionLink"
            itemId="sidebarItemSupportQuestion"
          />
        </MenuItem>

        {/* Statistics Menu */}
        <MenuItem text="Statistics" itemId="sidebarItemStatistics" icon="chart-line">
          <MenuItem visible text="Networks" hash="#networkstatistics" itemId="sidebarItemStatisticsNetwork" />
          <MenuItem visible text="Accounts" hash="#accountstatistics" itemId="sidebarItemStatisticsAccount" />
          <MenuItem visible text="Orders" hash="#orderstatistics" itemId="sidebarItemStatisticsOrder" />
          <MenuItem visible text="Series" hash="#seriesstatistics" itemId="sidebarItemStatisticsSeries" />
        </MenuItem>

        {/* SCP Changelog Management */}
        <MenuItem
          text="SCP Changelog"
          itemId="sidebarItemScpChangelogManagement"
          featureFlag="scp-changelog.enabled"
          icon="globe">
          <MenuItem
            text="List SCPs"
            microServiceRoute="/scp-changelog/list-scps"
            featureFlag="scp-changelog.scp"
            itemId="sidebarItemListScps"
          />
          <MenuItem
            text="SCP Details"
            microServiceRoute="/scp-changelog/scp-details"
            featureFlag="scp-changelog.scp"
            itemId="sidebarItemScpDetails"
          />
          <MenuItem
            text="List Releases"
            microServiceRoute="/scp-changelog/list-releases"
            featureFlag="scp-changelog.list_releases"
            itemId="sidebarItemListReleases"
          />
          <MenuItem
            text="Release Details"
            microServiceRoute="/scp-changelog/release-details"
            featureFlag="scp-changelog.release"
            itemId="sidebarItemReleaseDetails"
          />
          <MenuItem
            text="SCP Deployment Plan"
            microServiceRoute="/scp-changelog/list-release-schedules"
            featureFlag="scp-changelog.release_schedule"
            itemId="sidebarItemReleaseSchedules"
          />
        </MenuItem>

        {/* Admins Menu */}
        <MenuItem text="Administration" itemId="sidebarItemAdminMenu" icon="lock">
          <MenuItem text="Portal" itemId="sidebarItemAdminPortal">
            <MenuItem text="List Settings" hash="#listsettings" itemId="sidebarItemAdminListSettings" />
            <MenuItem text="Manage Banners" hash="#managebanners" itemId="sidebarItemAdminManageBanners" />
          </MenuItem>
          <MenuItem text="List API Keys" hash="#listapikeys" itemId="sidebarItemAdminListApiKeys" />
          <MenuItem text="Feature Flags" hash="#featureflags" itemId="sidebarItemAdminFeatureFlagsPage" />
          <MenuItem text="Access Management" itemId="sidebarItemAdminUsers">
            <MenuItem text="List Identity Providers" hash="#listidps" itemId="sidebarItemIdPs" />
            <MenuItem
              text="List User Permissions"
              hash="#listuserpermissions"
              itemId="sidebarItemAdminListUserPermissions"
            />
          </MenuItem>
          <MenuItem text="Organizations" itemId="sidebarItemAdminOrganizations">
            <MenuItem text="Move Accounts" hash="#adminmoveaccount" itemId="sidebarItemAdminMoveAccount" />
          </MenuItem>
          <MenuItem text="SIEM Create Key" hash="#siemcreatekey" itemId="sidebarItemSiemCreateKey" />
          <MenuItem
            text="4Wheels Guided Cluster Options"
            hash="#manage4wheelsclusteroptions"
            itemId="sidebarItemAdmin4Wheels"
          />
          <MenuItem
            text="Right Now Portal Queue Items"
            hash="#listrightnowportalqueueitems"
            itemId="sidebarItemAdminListRightNowPortalQueueItems"
          />
          <MenuItem
            text="Inactive Resource Management"
            hash="#inactivescanner"
            itemId="sidebarItemAdminInactiveScanner"
          />
        </MenuItem>
      </ul>

      <div id="sidebar-close-button" class="sidebar-close d-none d-md-block">
        <button type="button" class="btn btn-close-sidebar" title="Show/Hide Sidebar (alt+s)" onclick={toggleSidebar}>
          <i id="sidebar-close-button-icon" class="fas fa-chevron-left" />
        </button>
      </div>
    </div>
  );

  // Automatically collapse unrelated submenus when a submenu is expanded
  $(sidebar).on('show.bs.collapse', 'ul.sidebar-menu.collapse', function (event) {
    const item = $(this).parent();
    $(sidebar)
      .find('ul.sidebar-menu.collapse:not(:has(#' + item.attr('id') + '))')
      .collapse('hide');
    event.stopPropagation(); // don't bubble up to containing submenus
  });

  return sidebar;
}
